import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const hours = [
  { day: "monday", closed: true },
  { day: "tuesday", closed: false },
  { day: "wednesday", closed: false },
  { day: "thursday", closed: false },
  { day: "friday", closed: false },
];

export default function ContactPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="text-center">
          <span className="inline-block px-4 py-1.5 border border-primary-400/30 text-primary-400 text-xs md:text-sm font-medium rounded-full whitespace-nowrap mb-4">
            {t("contact.badge")}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 tracking-tight">
            {t("contact.heading")}
          </h1>
          <p className="text-sm md:text-base text-background-50/60 mt-4 max-w-xl mx-auto leading-relaxed">
            {t("contact.description")}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 md:px-6 pb-16 md:pb-24 max-w-5xl mx-auto">
        {/* Quick contact tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-6">
          <a
            href="tel:+41269195360"
            className="group bg-background-900/60 rounded-2xl border border-background-700 p-6 md:p-8 hover:border-primary-400/50 hover:bg-background-900 transition-all cursor-pointer"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-500/15 flex items-center justify-center shrink-0 group-hover:bg-primary-500/25 transition-colors">
                <i className="ri-phone-line text-primary-400 text-2xl md:text-3xl"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-background-50/60">
                  {t("contact.phoneLabel")}
                </p>
                <p className="text-lg md:text-2xl font-semibold text-background-50 mt-1 whitespace-nowrap">
                  +41 26 919 53 60
                </p>
              </div>
            </div>
          </a>
          <a
            href="mailto:service@clefsdelys.ch"
            className="group bg-background-900/60 rounded-2xl border border-background-700 p-6 md:p-8 hover:border-primary-400/50 hover:bg-background-900 transition-all cursor-pointer"
          >
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-500/15 flex items-center justify-center shrink-0 group-hover:bg-primary-500/25 transition-colors">
                <i className="ri-mail-line text-primary-400 text-2xl md:text-3xl"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm font-medium text-background-50/60">
                  {t("contact.emailLabel")}
                </p>
                <p className="text-base md:text-lg font-semibold text-background-50 mt-1 break-all">
                  service@clefsdelys.ch
                </p>
              </div>
            </div>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
          {/* Address */}
          <div className="bg-background-900/60 rounded-2xl border border-background-700 p-6 md:p-8">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-background-50 mb-6 flex items-center gap-2">
              <i className="ri-map-pin-line text-primary-400"></i>
              {t("contact.addressLabel")}
            </h2>
            <p className="text-sm md:text-base text-background-50/70 leading-relaxed">
              Rue Alexandre Cailler 6
              <br />
              1636 Broc
              <br />
              Switzerland
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Rue%20Alexandre%20Cailler%206%2C%201636%20Broc%2C%20Switzerland"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-background-800/60 border border-background-600 rounded-full text-sm font-medium text-background-50 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-navigation-line text-base"></i>
              {t("contact.getDirections")}
            </a>
          </div>

          {/* Hours */}
          <div className="bg-background-900/60 rounded-2xl border border-background-700 p-6 md:p-8">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-background-50 mb-6 flex items-center gap-2">
              <i className="ri-time-line text-primary-400"></i>
              {t("contact.hoursLabel")}
            </h2>
            <div className="flex flex-col gap-1">
              {hours.map((h) => (
                <div
                  key={h.day}
                  className="flex items-center justify-between py-3 border-b border-background-700/50 last:border-b-0"
                >
                  <span className="text-sm font-medium text-background-50/80">
                    {t(`contact.days.${h.day}`)}
                  </span>
                  <span className={h.closed ? "text-sm text-background-50/35" : "text-sm text-background-50/70 whitespace-nowrap"}>
                    {h.closed ? t("contact.closed") : t("contact.hoursSlot")}
                  </span>
                </div>
              ))}
              <p className="mt-4 pt-3 border-t border-background-700/50 text-xs text-background-50/35 italic">
                {t("contact.holidaysNote")}
              </p>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-6 bg-background-900/60 rounded-2xl border border-background-700 overflow-hidden">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2740.1234567890123!2d7.123456789012345!3d46.12345678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c6e1234567890%3A0x1234567890abcdef!2sRue%20Alexandre%20Cailler%206%2C%201636%20Broc%2C%20Switzerland!5e0!3m2!1sen!2sch!4v1234567890123!5m2!1sen!2sch"
            width="100%"
            height="340"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Clefs de Lys SA Location"
            className="grayscale-[30%]"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}